import { Eye } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';
import type { ResumenCajaRow } from '../api';
import { LlevaFacturaTag } from './LlevaFacturaTag';
import { CajaNombre } from './CajaNombre';
import { etiquetaCaja } from '@/shared/lib/cajasEfectivo';

/**
 * Vista índice de Tesorería: una fila por caja con su saldo, los ingresos y
 * egresos del período activo y la fecha del último cierre. Reemplaza el
 * combo de cajas como puerta de entrada — el ojo abre el detalle.
 *
 * Las cifras son las MISMAS que el footer del detalle de cada caja (las dos
 * salen de `computeSaldoSeries` en el server), y son del **período activo**:
 * desde la última Apertura, no del mes calendario. Por eso la columna se
 * rotula así — si dice sólo "Ingresos" alguien lo lee como "del mes".
 */
export function CajasResumenTable({
  rows,
  loading,
  error,
  onView,
  onRetry,
}: {
  rows: ResumenCajaRow[];
  loading: boolean;
  error: boolean;
  onView: (caja: string) => void;
  onRetry: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Caja</Th>
              {/* Ancho fijo para que la moneda y el chip "de ARS" entren en
                  una sola línea: con w-24 el chip caía debajo. */}
              <Th className="w-36">Moneda</Th>
              <Th className="text-right">Saldo calculado</Th>
              <Th className="text-right">Ingresos período</Th>
              <Th className="text-right">Egresos período</Th>
              <Th className="w-36">Último cierre</Th>
              <Th className="w-20 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={7} />
            ) : error ? (
              <RowStatus cols={7}>
                <span className="text-destructive">No se pudieron cargar las cajas.</span>
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Reintentar
                </Button>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={7}>No hay cajas activas para este país.</RowStatus>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.caja}
                  className="cursor-pointer transition-colors hover:bg-muted/40"
                  onClick={() => onView(r.caja)}
                >
                  <Td>
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      <CajaNombre caja={r.caja} tipo={r.tipo} />
                      <LlevaFacturaTag show={r.llevaFactura} />
                    </span>
                  </Td>
                  <Td className="text-muted-foreground">
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      {r.moneda}
                      <ConvertidaBadge desde={r.convertidaDesde} />
                    </span>
                  </Td>
                  <Td className="text-right font-semibold tabular-nums">
                    {formatMoney(r.saldo, r.moneda)}
                  </Td>
                  <Td className="text-right tabular-nums text-emerald-600">
                    {formatMoney(r.ingresos, r.moneda)}
                  </Td>
                  <Td className="text-right tabular-nums text-rose-600">
                    {formatMoney(r.egresos, r.moneda)}
                  </Td>
                  <Td>
                    <UltimoCierre fecha={r.ultimoCierre} />
                  </Td>
                  <Td className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        onView(r.caja);
                      }}
                      aria-label={`Ver movimientos de ${etiquetaCaja(r.caja)}`}
                      title="Ver movimientos"
                    >
                      <Eye className="h-4 w-4" aria-hidden />
                    </Button>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
            <span>No se pudieron cargar las cajas.</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Reintentar
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No hay cajas activas para este país.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <li key={r.caja}>
                <button
                  type="button"
                  onClick={() => onView(r.caja)}
                  className="flex w-full flex-col gap-2 px-4 py-3 text-left transition-colors active:bg-muted/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                      <CajaNombre caja={r.caja} tipo={r.tipo} />
                      <LlevaFacturaTag show={r.llevaFactura} />
                    </span>
                    <span className="flex items-center gap-1.5">
                      <ConvertidaBadge desde={r.convertidaDesde} />
                      <span className="text-base font-semibold tabular-nums">
                        {formatMoney(r.saldo, r.moneda)}
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="text-emerald-600">
                      + {formatMoney(r.ingresos, r.moneda)}
                    </span>
                    <span className="text-rose-600">
                      − {formatMoney(r.egresos, r.moneda)}
                    </span>
                    <span>·</span>
                    <UltimoCierre fecha={r.ultimoCierre} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Marca las filas que se convirtieron a dólares. En la vista USD conviven
 * cajas convertidas con cajas que SIEMPRE operaron en dólares (Efectivo
 * Dolar, Transferencia Galicia Dolar) y sin la marca las dos se leen igual.
 * El título muestra el saldo original, que es lo que uno quiere chequear.
 */
function ConvertidaBadge({
  desde,
}: {
  desde: ResumenCajaRow['convertidaDesde'];
}) {
  if (!desde) return null;
  return (
    <span
      className="inline-flex shrink-0 rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-4 tracking-wide text-muted-foreground"
      title={`Convertida desde ${formatMoney(desde.saldo, desde.moneda)}`}
    >
      de {desde.moneda}
    </span>
  );
}

/**
 * "Sin cierres" no es un caso borde: al 2026-09-04 Nova Banorte y Nova
 * Mercado Pago nunca se cerraron.
 */
function UltimoCierre({ fecha }: { fecha: string | null }) {
  if (!fecha) return <span className="text-muted-foreground">Sin cierres</span>;
  return <span className="text-foreground">{fecha}</span>;
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-3 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-3 align-middle', className)}>{children}</td>;
}

function RowStatus({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-3 py-3.5">
              <Skeleton className="h-4 w-full max-w-[120px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
